// src/pages/Vendor/Products/index.tsx
import React, { useState, useEffect } from 'react';
import { productsAPI } from '../../../api/products';
import DeleteProductModal from '../../../components/products/DeleteProductModal';
import ProductFormModal from '../../../components/products/ProductFormModal';
import ProductList from '../../../components/products/ProductList';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import showToast from '../../../utils/toast';
import toast from '../../../utils/toast';
import { AlertTriangle } from 'lucide-react';
import { useProductFilters } from '../../../hooks/useProductFilters';
import type { Product } from '../../../types';
import { storesAPI } from '../../../api/stores';

const VendorProducts: React.FC = () => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [productToDelete, setProductToDelete] = useState<any>(null);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [showResubmitModal, setShowResubmitModal] = useState(false);
    const [hasMultipleStores, setHasMultipleStores] = useState(false);
    const [productToSubmit, setProductToSubmit] = useState<any>(null);
    const [isResubmit, setIsResubmit] = useState(false);
    const [productAction, setProductAction] = useState<any>(null);
    const [modalMode, setModalMode] = useState<'view' | 'edit' | 'review' | 'create'>('edit');
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        title: string;
        message: React.ReactNode;
        confirmText?: string;
        cancelText?: string;
        type?: 'warning' | 'success' | 'danger' | 'info';
        onConfirm: () => void;
        onClose: () => void;
    }>({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        cancelText: 'Cancel',
        type: 'warning',
        onConfirm: () => { },
        onClose: () => { },
    });
    const {
        products,
        setProducts,
        loading,
        pagination,
        statusFilter,
        searchTerm,
        handleStatusFilterChange,
        handleSearchChange,
        handlePageChange,
        fetchData,
    } = useProductFilters({
        fetchProducts: async (page, status, search) => {
            const params: any = {
                page: page,
                page_size: 20,
            };

            if (status === 'archived') {
                params.status = 'archived';
            } else if (status !== 'all' && status !== 'active') {
                params.status = status;
            }

            if (search?.trim()) {
                params.searchTerm = search.trim();
            }

            const response = await productsAPI.getVendorProducts(params);
            return {
                data: response.data?.results || response.data || [],
                total: response.data?.count || 0,
                pages: Math.ceil((response.data?.count || 0) / 20),
            };
        },
        initialStatus: 'all',
    });

    useEffect(() => {
        const checkStores = async () => {
            try {
                const stores = await storesAPI.getStores();
                setHasMultipleStores(stores.length > 1);
            } catch (error) {
                console.error('Failed to check stores:', error);
            }
        };
        checkStores();
    }, []);

    const handleProductAction = (action: string, product: any) => {
        switch (action) {
            case 'edit':
                setSelectedProduct(product);
                setModalMode('edit');
                setShowFormModal(true);
                break;
            case 'review':
                setSelectedProduct(product);
                setModalMode('review');
                setShowFormModal(true);
                break;
            case 'submit':
                setProductAction(product);
                setShowSubmitModal(true);
                break;
            case 'resubmit':
                setProductAction(product);
                setShowResubmitModal(true);
                break;
        }
    };

    const handleDelete = async (productId: number) => {
        try {
            const response = await productsAPI.deleteProduct(productId);
            if (!response.error) {
                toast.success('Product deleted successfully');
                fetchData();
            }
        } catch (error) {
            toast.error('Failed to delete product');
        }
        setShowDeleteModal(false);
        setSelectedProduct(null);
    };

    const handleSubmitConfirm = async () => {
        if (!productToSubmit) return;

        try {
            let response;
            if (isResubmit) {
                // Use resubmit endpoint
                response = await productsAPI.resubmitProduct(productToSubmit.id);
            } else {
                // Use regular update for initial submit
                response = await productsAPI.updateProduct(productToSubmit.id, {
                    status: 'pending_review',
                    submitted_for_review_at: new Date().toISOString(),
                });
            }

            if (!response.error) {
                toast.success(
                    isResubmit ? 'Product resubmitted for review' : 'Product submitted for review'
                );
                fetchData();
                setShowSubmitModal(false);
                setProductToSubmit(null);
            }
        } catch (error) {
            toast.error('Failed to submit product');
        }
    };

    const handleSubmitForReview = (product: any) => {
        setProductToSubmit(product);
        setIsResubmit(product.status === 'changes_requested');
        setShowSubmitModal(true);
    };

    const handleRowClick = async (product: any) => {
        try {
            // ✅ Fetch the latest product data BEFORE opening modal
            const response = await productsAPI.getVendorProduct(product.id);
            const updatedProduct = response.data;

            // ✅ Update the product in the list
            setProducts(prevProducts =>
                prevProducts.map(p =>
                    p.id === updatedProduct.id ? updatedProduct : p
                )
            );

            // ✅ Determine mode based on status
            const modeMap: Record<string, 'edit' | 'review' | 'view'> = {
                'draft': 'edit',
                'rejected': 'view',
                'changes_requested': 'edit',
                'pending_review': 'review',
            };
            const mode = modeMap[updatedProduct.status] || 'view';

            // ✅ Now open the modal with the updated product
            setSelectedProduct(updatedProduct);
            setModalMode(mode);
            setShowFormModal(true);
        } catch (error) {
            console.error('Failed to fetch product:', error);
            toast.error('Failed to load product details');
            // Fallback to existing product if fetch fails
            const modeMap: Record<string, 'edit' | 'review' | 'view'> = {
                'draft': 'edit',
                'rejected': 'view',
                'changes_requested': 'edit',
                'pending_review': 'review',
            };
            const mode = modeMap[product.status] || 'view';
            setSelectedProduct(product);
            setModalMode(mode);
            setShowFormModal(true);
        }
    };

    const handleProductStatusChange = (productId: number, isActive: boolean) => {
        setProducts(prevProducts =>
            prevProducts.map(product =>
                product.id === productId
                    ? { ...product, is_active: isActive }
                    : product
            )
        );
    };

    const handleArchive = async (product: Product) => {
        try {
            const response = await productsAPI.archiveProduct(product.id);
            if (response.data.success) {
                toast.success(`"${product.title}" has been archived`);
                fetchData(); // Refresh list
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to archive product');
        }
    };

    const handleUnarchive = async (product: Product) => {
        try {
            const response = await productsAPI.unarchiveProduct(product.id);
            if (response.data.success) {
                fetchData(); // Refresh list
                toast.success(`"${product.title}" has been restored`);
            }
        } catch (error) {
            toast.error(error.response?.data?.error || 'Failed to restore product');
        }
    };

    const handleArchiveClick = (product: Product) => {
        setConfirmModal({
            isOpen: true,
            title: `Archive "${product.title}"?`,
            message: (
                <div className="space-y-2">
                    <p>This product will be moved to archives and hidden from the store.</p>
                    <p className="text-sm text-gray-600">You can restore it at any time.</p>
                    <p className="text-sm text-amber-600 flex items-center gap-1.5">
                        <AlertTriangle className="h-4 w-4" />
                        This will remove all variants from the storefront.
                    </p>
                </div>
            ),
            confirmText: 'Yes, Archive',
            cancelText: 'Cancel',
            type: 'warning',
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                handleArchive(product);
            },
            onClose: () => setConfirmModal(prev => ({ ...prev, isOpen: false })),
        });
    };

    const handleRollbackChanges = async (product: Product) => {
        try {
            await productsAPI.rollbackChanges(product.id);
            toast.success('Changes discarded successfully');
            fetchData(pagination.page, true); // Silent refresh
        } catch (error) {
            toast.error('Failed to discard changes');
        }
    };

    const handleDiscardChanges = (product: Product) => {
        const hasChanges = product?.can_resubmit;

        setConfirmModal({
            isOpen: true,
            title: hasChanges ? 'Discard Changes?' : 'Reject Changes?',
            message: hasChanges ? (
                <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                        You have made changes since the last review request.
                    </p>
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-red-700">
                            <strong>What will happen:</strong>
                        </p>
                        <ul className="text-sm text-red-600 mt-1 space-y-1 list-disc list-inside">
                            <li>All pending changes will be permanently discarded</li>
                            <li>The product will be rolled back to the last approved version</li>
                            <li>This action cannot be undone</li>
                        </ul>
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <p className="text-sm text-gray-700">
                        You haven't made any changes since the last review request.
                    </p>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-700">
                            <strong>What will happen:</strong>
                        </p>
                        <ul className="text-sm text-yellow-600 mt-1 space-y-1 list-disc list-inside">
                            <li>The product will be reactivated</li>
                            <li>No changes will be lost (there were none)</li>
                            <li>The product will go back to the approved state</li>
                        </ul>
                    </div>
                </div>
            ),
            confirmText: hasChanges ? 'Yes, Discard Changes' : 'Yes, Reactivate Product',
            cancelText: 'Cancel',
            type: hasChanges ? 'danger' : 'info',
            onConfirm: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
                handleRollbackChanges(product);
                setShowFormModal(false);
            },
            onClose: () => {
                setConfirmModal(prev => ({ ...prev, isOpen: false }));
            }
        });
    };

    return (
        <div className="space-y-6">
            {/* Products Table */}
            <ProductList
                products={products}
                loading={loading}
                onRefresh={fetchData}
                showVendorColumn={false}
                showCategoriesColumn={true}
                showStoreColumn={hasMultipleStores}
                actions={['view', 'edit', 'submit', 'resubmit', 'delete', 'archive', 'rollback']}
                onProductEdit={(product) => {
                    setSelectedProduct(product);
                    setModalMode('edit');
                    setShowFormModal(true);
                }}
                onProductView={(product) => {
                    setSelectedProduct(product);
                    setModalMode('view');
                    setShowFormModal(true);
                }}
                onProductDelete={(product) => {
                    setProductToDelete(product);
                    setShowDeleteModal(true);
                }}
                onAddProduct={() => {
                    setSelectedProduct(null);
                    setModalMode('create');
                    setShowFormModal(true);
                }}
                onProductSubmit={handleSubmitForReview}
                onProductResubmit={handleSubmitForReview}
                onProductArchive={handleArchiveClick}
                onProductUnarchive={handleUnarchive}
                onRollbackChanges={handleDiscardChanges}
                isVendor={true}
                showAddButton={true}
                rowClickMode="edit"  // This triggers handleRowClick
                onRowClick={handleRowClick}
                onAction={handleProductAction}
                title="My Products"
                emptyMessage="You haven't added any products yet"
                pagination={pagination}
                onPageChange={handlePageChange}
                statusFilter={statusFilter}
                onFilterChange={handleStatusFilterChange}
                onSearchChange={handleSearchChange}
                searchTerm={searchTerm}
            />

            {/* Modals */}
            {showFormModal && (
                <ProductFormModal
                    product={selectedProduct}
                    onClose={() => {
                        setShowFormModal(false);
                        setSelectedProduct(null);
                    }}
                    onSuccess={() => {
                        fetchData();
                        setShowFormModal(false);
                        setSelectedProduct(null);
                    }}
                    isVendor={true}
                    mode={modalMode}
                    onProductStatusChange={handleProductStatusChange}
                    onRollbackChanges={() => {
                        handleDiscardChanges(selectedProduct);
                    }}
                />
            )}
            {showDeleteModal && productToDelete && (
                <DeleteProductModal
                    product={productToDelete}
                    onConfirm={() => handleDelete(productToDelete.id)}
                    onClose={() => {
                        setShowDeleteModal(false);
                        setProductToDelete(null);
                    }}
                />
            )}
            {showSubmitModal && (
                <ConfirmationModal
                    isOpen={showSubmitModal}
                    onClose={() => setShowSubmitModal(false)}
                    onConfirm={handleSubmitConfirm}
                    title="Submit for Review?"
                    message={`Are you sure you want to submit "${productToSubmit?.title}" for review? You won't be able to edit it until the review is complete.`}
                    confirmText="Submit"
                    type="info"
                />
            )}

            {/* Confirmation Modal */}
            {confirmModal.isOpen && (
                <ConfirmationModal
                    isOpen={confirmModal.isOpen}
                    onClose={confirmModal.onClose}
                    onConfirm={confirmModal.onConfirm}
                    title={confirmModal.title}
                    message={confirmModal.message}
                    confirmText={confirmModal.confirmText}
                    cancelText={confirmModal.cancelText}
                    type={confirmModal.type}
                />
            )}
        </div>
    );
};

export default VendorProducts;